// src/components/SurveyForm/QuestionRenderer/QuestionRenderer.jsx

import React, { Fragment } from 'react';
import { Divider, FormControl, FormLabel, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

// Importar utils
import {
  isBigSelection,
  isBipolarSlider,
  isCheckbox,
  isComplex,
  isConstantAdd,
  isDate,
  isENPSPromoter,
  isFile,
  isInformativeText,
  isMatrix,
  isMultimedia,
  isRadio,
  isRadioFace,
  isRange,
  isRelational,
  isSelect,
  isSlider,
  isTable,
  isText,
} from '../../../utils/questionTypeUtils';
import { parseTextWithStyles } from '../../SurveyForm/utils/textParserUtils';
import BigSelectionQuestion from '../BigSelectionQuestion/BigSelectionQuestion';
import BipolarSliderQuestion from '../BipolarSliderQuestion/BipolarSliderQuestion';
import CheckBoxQuestion from '../CheckBoxQuestion/CheckBoxQuestion';
import ComplexQuestion from '../ComplexQuestion/ComplexQuestion';
import ConstantSumQuestion from '../ConstantSumQuestion/ConstantSumQuestion';
import DateQuestion from '../DateQuestion/DateQuestion';
import FileUploadAnswer from '../FileQuestion/FileQuestion';
import LikertFaceQuestion from '../LikertFaceQuestion/LikertFaceQuestion';
import MatrizQuestion from '../MatrizQuestion';
import { MediaQuestion } from '../MediaQuestion/MediaQuestion';
import NPSSliderQuestion from '../NPSSliderQuestion/NPSSliderQuestion';
import OpinionScaleQuestion from '../OpinionScaleQuestion/OpinionScaleQuestion';
// Importar todos los componentes de preguntas
import RadioQuestion from '../RadioQuestion/RadioQuestion';
import RangeQuestion from '../RangeQuestion/RangeQuestion';
import { RelationalQuestion } from '../RelationalQuestion/RelationalQuestion';
import SelectQuestion from '../SelectQuestion/SelectQuestion';
import TableQuestion from '../TableQuestion/TableQuestion';
import TextQuestion from '../TextQuestion/TextQuestion';

import styles from '../../SurveyForm/SurveyForm.module.css';

/**
 * Componente que renderiza dinámicamente el tipo correcto de pregunta
 */
const QuestionRenderer = ({
  question,
  index,
  activeStep,
  pagination,
  visibleQuestions,
  isFirstVisibleQuestionInGroup,
  pausedQuestions,
  // Estados y handlers
  formValues,
  unansweredQuestions,
  submitAttempted,
  otherOptionValues,
  selectedRadioOptions,
  values,
  sliderErrors,
  primaryColor,
  secondaryColor,
  questionRefs,
  matrizRefs,
  // Handlers de cambio
  handleRadioChange,
  handleCheckboxChange,
  handleRangeChange,
  handleChangeSlider,
  handleChangeDate,
  handleRelationalChange,
  handleSliderSumaConstante,
  handleComplexAnswerChange,
  handleOtherCheckboxInputChange,
  updateVisibility,
  setFormValues,
  setUnansweredQuestions,
  setOtherOptionValues,
  setSliderErrors,
  // Funciones auxiliares
  getOptions,
  getLikertIcon,
  calculateValue,
  sliderColor,
  marks,
  textBipolarBar,
  // Voice recognition
  browserSupportsSpeechRecognition,
  dictatingIndex,
  isRecording,
  recordingTime,
  resetCount,
  MAX_RESETS,
  startDictation,
  pauseDictation,
  resumeDictation,
  stopDictation,
  canRecord,
  getMaxRecordingTime,
  getRecordingTooltip,
  formatTime,
  getTimeColor,
  showUploadError,
  fontFamily,
  transcript,
  baseTextRef,
  hasOwnBold,
}) => {
  const {
    questionId,
    questionName: rawQuestionName,
    typeQuestion,
    description,
    options = [],
    score,
    settings,
    conditional,
    urlParam,
    matrixOption,
    childrenQuestions,
    multimedia,
    selectOptions,
    tableSetting,
  } = question;

  const questionName =
    rawQuestionName
      ?.replace(/<p>/g, '')
      .replace(/<\/p>/g, ' ')
      .replace(/<br\s*\/?>/g, ' ')
      .trim() || '';
  /**
   * Determina si la pregunta debe mostrarse
   */
  const shouldDisplay = () => {
    return (
      index >= activeStep * pagination && index < (activeStep + 1) * pagination
    );
  };

  /**
   * Obtiene el className para la pregunta
   */
  const getQuestionClassName = () => {
    return visibleQuestions[index] && shouldDisplay()
      ? ''
      : styles.hiddenQuestion;
  };

  /**
   * Renderiza el componente de pregunta según su tipo
   */
  const renderQuestionComponent = () => {
    // RADIO
    if (isRadio(typeQuestion)) {
      return (
        <RadioQuestion
          questionName={questionName}
          description={description}
          options={options}
          currentValue={formValues[index]?.value || ''}
          otherValue={otherOptionValues[questionId] || ''}
          questionId={questionId}
          typeQuestion={typeQuestion}
          index={index}
          conditional={conditional}
          hasError={unansweredQuestions.includes(index)}
          submitAttempted={submitAttempted}
          primaryColor={primaryColor}
          questionRef={questionRefs[questionId]}
          selectedRadioOptions={selectedRadioOptions}
          hasOwnBold={hasOwnBold}
          onValueChange={(selectedValue, selectedOption) => {
            handleRadioChange(
              { target: { value: selectedValue } },
              index,
              conditional,
              selectedOption?.childrenQuestionNumber,
            );
          }}
          onOtherInputChange={(e) => {
            const inputValue = e.target.value;

            // Solo actualizar otherOptionValues
            setOtherOptionValues((prev) => ({
              ...prev,
              [questionId]: inputValue,
            }));

            setFormValues((prev) => {
              const updated = [...prev];
              updated[index].value = inputValue;
              return updated;
            });

            // Marcar como respondida si tiene texto
            if (inputValue.trim() !== '') {
              setUnansweredQuestions((prevUnanswered) =>
                prevUnanswered.filter(
                  (unansweredIndex) => unansweredIndex !== index,
                ),
              );
            } else {
              // Si borra el texto, volver a marcar como sin responder
              setUnansweredQuestions((prev) => {
                if (!prev.includes(index)) {
                  return [...prev, index];
                }
                return prev;
              });
            }
          }}
        />
      );
    }

    // LIKERT FACE
    if (isRadioFace(typeQuestion)) {
      return (
        <LikertFaceQuestion
          questionName={questionName}
          description={description}
          options={options}
          currentValue={formValues[index]?.value || ''}
          questionId={questionId}
          typeQuestion={typeQuestion}
          index={index}
          hasError={unansweredQuestions.includes(index)}
          primaryColor={primaryColor}
          questionRef={questionRefs[questionId]}
          getLikertIcon={getLikertIcon}
          hasOwnBold={hasOwnBold}
          onValueChange={(selectedValue) => {
            handleRadioChange({ target: { value: selectedValue } }, index);
          }}
        />
      );
    }

    // CHECKBOX

    if (isCheckbox(typeQuestion)) {
      return (
        <CheckBoxQuestion
          questionName={questionName}
          description={description}
          options={options}
          formValue={formValues[index]}
          otherValue={otherOptionValues[questionId] || ''}
          questionId={questionId}
          index={index}
          score={score}
          hasError={unansweredQuestions.includes(index)}
          submitAttempted={submitAttempted}
          primaryColor={primaryColor}
          questionRef={questionRefs[questionId]}
          hasOwnBold={hasOwnBold}
          onCheckboxChange={(event) => {
            handleCheckboxChange(event, index);
          }}
          onOtherInputChange={(e) => {
            const inputValue = e.target.value;

            // Actualizar el estado del input
            setOtherOptionValues((prev) => ({
              ...prev,
              [questionId]: inputValue,
            }));

            // ✅ CORREGIR: Usar 'question' en lugar de 'questions[index]'
            setFormValues((prevFormValues) => {
              const updated = [...prevFormValues];
              updated[index].value = inputValue; // ← Para checkbox usamos .otherValue
              updated[index].nameQuestion = questionName; // ← Usar questionName del prop
              return updated;
            });

            // Marcar como respondida si tiene texto
            if (inputValue.trim() !== '') {
              setUnansweredQuestions((prevUnanswered) =>
                prevUnanswered.filter(
                  (unansweredIndex) => unansweredIndex !== index,
                ),
              );
            } else {
              // Si borra el texto, volver a marcar como sin responder
              setUnansweredQuestions((prev) => {
                if (!prev.includes(index)) {
                  return [...prev, index];
                }
                return prev;
              });
            }
          }}
        />
      );
    }

    // RANGE
    if (isRange(typeQuestion)) {
      return (
        <RangeQuestion
          questionName={questionName}
          description={description}
          score={score}
          currentValue={formValues[index].value}
          index={index}
          questionId={questionId}
          hasError={unansweredQuestions.includes(index)}
          primaryColor={primaryColor}
          questionRef={questionRefs[questionId]}
          hasOwnBold={hasOwnBold}
          onValueChange={(newValue) => {
            handleRangeChange(newValue, index);
          }}
        />
      );
    }

    // TEXT
    if (isText(typeQuestion)) {
      return (
        <TextQuestion
          questionId={questionId}
          hasOwnBold={hasOwnBold}
          typeQuestion={typeQuestion}
          questionName={questionName}
          description={description}
          index={index}
          unansweredQuestions={unansweredQuestions}
          questionRef={questionRefs[questionId]}
          formValue={formValues[index]}
          onChange={handleRadioChange}
          settings={settings}
          sliderErrors={sliderErrors}
          pausedQuestions={pausedQuestions}
          onErrorClear={(idx) => {
            setSliderErrors((prev) => {
              const updated = { ...prev };
              delete updated[idx];
              return updated;
            });
          }}
          primaryColor={primaryColor}
          browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
          dictatingIndex={dictatingIndex}
          isRecording={isRecording}
          recordingTime={recordingTime}
          resetCount={resetCount}
          maxResets={MAX_RESETS}
          onStartDictation={startDictation}
          onPauseDictation={pauseDictation}
          onResumeDictation={resumeDictation}
          onStopDictation={stopDictation}
          canRecord={canRecord}
          getMaxRecordingTime={getMaxRecordingTime}
          getRecordingTooltip={getRecordingTooltip}
          formatTime={formatTime}
          getTimeColor={getTimeColor}
          showDivider={true}
        />
      );
    }

    // INFORMATIVE TEXT
    if (isInformativeText(typeQuestion)) {
      return (
        <div
          style={{
            backgroundColor: '#f5f5f5',
            padding: '1em',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '1.5em',
          }}
        >
          <FormLabel
            style={{
              fontSize: '1.1rem',
              marginBottom: '0.5em',
              display: 'block',
            }}
          >
            {/*parseTextWithStyles(questionName)*/}
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(rawQuestionName),
              }}
            />
          </FormLabel>
          <Typography
            variant="caption"
            style={{
              display: 'block',
              color: 'rgba(0, 0, 0, 0.7)',
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(description),
              }}
            />
            {/*parseTextWithStyles(description)*/}
          </Typography>
        </div>
      );
    }

    // NPS SLIDER
    if (isSlider(typeQuestion)) {
      return (
        <NPSSliderQuestion
          questionId={questionId}
          typeQuestion={typeQuestion}
          questionName={questionName}
          description={description}
          index={index}
          unansweredQuestions={unansweredQuestions}
          questionRef={questionRefs[questionId]}
          value={values[index]}
          onChange={handleChangeSlider}
          marks={marks}
          calculateValue={calculateValue}
          getSliderColor={sliderColor}
          showDivider={true}
          hasOwnBold={hasOwnBold}
        />
      );
    }

    // ENPS PROMOTER
    if (isENPSPromoter(typeQuestion)) {
      return (
        <OpinionScaleQuestion
          questionId={questionId}
          typeQuestion={typeQuestion}
          questionName={questionName}
          description={description}
          index={index}
          unansweredQuestions={unansweredQuestions}
          questionRef={questionRefs[questionId]}
          value={values[index]}
          onChange={handleChangeSlider}
          textBipolarBar={textBipolarBar}
          primaryColor={primaryColor}
          showDivider={true}
          hasOwnBold={hasOwnBold}
        />
      );
    }

    // BIPOLAR SLIDER
    if (isBipolarSlider(typeQuestion)) {
      return (
        <BipolarSliderQuestion
          questionId={questionId}
          typeQuestion={typeQuestion}
          questionName={questionName}
          description={description}
          index={index}
          unansweredQuestions={unansweredQuestions}
          questionRef={questionRefs[questionId]}
          score={score}
          value={values[index]}
          onChange={handleChangeSlider}
          textBipolarBar={textBipolarBar}
          primaryColor={primaryColor}
          showDivider={true}
          hasOwnBold={hasOwnBold}
        />
      );
    }

    // SELECT
    if (isSelect(typeQuestion)) {
      return (
        <SelectQuestion
          questionName={questionName}
          hasOwnBold={hasOwnBold}
          description={description}
          options={getOptions(options, questionId)}
          currentValue={formValues[index]?.value || ''}
          questionId={questionId}
          typeQuestion={typeQuestion}
          index={index}
          urlParam={urlParam}
          hasError={unansweredQuestions.includes(index)}
          questionRef={questionRefs[questionId]}
          onValueChange={(selectedValue) => {
            handleRadioChange(
              { target: { value: selectedValue } },
              index,
              urlParam,
            );
          }}
        />
      );
    }

    // DATE
    if (isDate(typeQuestion)) {
      return (
        <DateQuestion
          questionId={questionId}
          typeQuestion={typeQuestion}
          questionName={questionName}
          description={description}
          index={index}
          unansweredQuestions={unansweredQuestions}
          questionRef={questionRefs[questionId]}
          value={values[index]}
          onChange={handleChangeDate}
          primaryColor={primaryColor}
          showDivider={true}
          hasOwnBold={hasOwnBold}
        />
      );
    }

    // RELATIONAL
    if (isRelational(typeQuestion)) {
      return (
        <Fragment>
          <FormLabel
            style={{
              fontSize: '1.1em',
              fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
              marginBottom: '1.1em',
              color: unansweredQuestions.includes(index)
                ? 'red'
                : 'rgba(0, 0, 0, 0.6)',
            }}
            ref={questionRefs[questionId]}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(questionName),
              }}
            />
          </FormLabel>
          <Typography
            variant="caption"
            style={{ display: 'block', fontStyle: 'italic' }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(description),
              }}
            />
          </Typography>
          <RelationalQuestion
            options={options}
            selectOptions={selectOptions}
            handleRelationalChange={handleRelationalChange}
            indexQuestion={index}
            unansweredQuestions={unansweredQuestions}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
          <Divider variant="middle" sx={{ marginTop: '10px' }} />
        </Fragment>
      );
    }

    // CONSTANT SUM
    if (isConstantAdd(typeQuestion)) {
      return (
        <ConstantSumQuestion
          questionId={questionId}
          typeQuestion={typeQuestion}
          questionName={questionName}
          description={description}
          index={index}
          unansweredQuestions={unansweredQuestions}
          questionRef={questionRefs[questionId]}
          options={options}
          score={score}
          formValue={formValues[index] || {}}
          onChange={handleSliderSumaConstante}
          submitAttempted={submitAttempted}
          sliderErrors={sliderErrors}
          showDivider={true}
          hasOwnBold={hasOwnBold}
        />
      );
    }

    // MATRIX
    if (isMatrix(typeQuestion)) {
      return (
        <Fragment>
          <FormLabel
            style={{
              fontSize: '1.1em',
              fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
              marginBottom: '1.1em',
              color: unansweredQuestions.includes(index)
                ? 'red'
                : 'rgba(0, 0, 0, 0.6)',
            }}
            ref={questionRefs[questionId]}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(questionName),
              }}
            />
          </FormLabel>
          <Typography
            variant="caption"
            style={{ display: 'block', fontStyle: 'italic' }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(description),
              }}
            />
          </Typography>
          <MatrizQuestion
            ref={(el) => (matrizRefs.current[questionId] = el)}
            matrixOption={matrixOption}
            formValues={formValues[index]}
            index={index}
            setFormValues={setFormValues}
            questionName={questionName}
            description={description}
            primaryColor={primaryColor}
            fontFamily={fontFamily}
          />
          <Divider variant="middle" sx={{ marginTop: '10px' }} />
        </Fragment>
      );
    }

    // BIG SELECTION
    if (isBigSelection(typeQuestion)) {
      return (
        <BigSelectionQuestion
          questionName={questionName}
          hasOwnBold={hasOwnBold}
          description={description}
          options={options}
          settings={settings}
          formValue={formValues[index]}
          index={index}
          questionId={questionId}
          conditional={conditional}
          unansweredQuestions={unansweredQuestions}
          questionRef={questionRefs[questionId]}
          onValueChange={(newData) => {
            const newFormValues = [...formValues];
            newFormValues[index] = {
              ...newFormValues[index],
              ...newData,
              nameQuestion: questionName,
            };
            setFormValues(newFormValues);
            setUnansweredQuestions((prev) =>
              (Array.isArray(prev) ? prev : []).filter(
                (unansweredIndex) => unansweredIndex !== index,
              ),
            );
          }}
          onVisibilityUpdate={(childrenQuestionNumber) => {
            if (conditional) {
              updateVisibility(index, childrenQuestionNumber);
            }
          }}
          fontFamily={fontFamily}
        />
      );
    }

    // COMPLEX
    if (isComplex(typeQuestion)) {
      return (
        <ComplexQuestion
          ref={questionRefs[questionId]}
          questionData={{
            questionId,
            questionName,
            description,
            options,
            childrenQuestions: childrenQuestions || [],
          }}
          onAnswerChange={(answers) =>
            handleComplexAnswerChange(answers, index)
          }
          primaryColor={primaryColor}
          hasError={unansweredQuestions.includes(index)}
          questionRef={questionRefs[questionId]}
          getLikertIcon={getLikertIcon}
          fontFamily={fontFamily}
          browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
          dictatingIndex={dictatingIndex}
          isRecording={isRecording}
          recordingTime={recordingTime}
          resetCount={resetCount}
          maxResets={MAX_RESETS}
          pausedQuestions={pausedQuestions}
          onStartDictation={startDictation}
          onPauseDictation={pauseDictation}
          onResumeDictation={resumeDictation}
          onStopDictation={stopDictation}
          canRecord={canRecord}
          getMaxRecordingTime={getMaxRecordingTime}
          getRecordingTooltip={getRecordingTooltip}
          formatTime={formatTime}
          getTimeColor={getTimeColor}
          hasOwnBold={hasOwnBold}
          transcript={transcript}
          baseTextRef={baseTextRef}
        />
      );
    }

    // MULTIMEDIA
    if (isMultimedia(typeQuestion)) {
      return (
        <MediaQuestion
          ref={questionRefs[questionId]}
          hasOwnBold={hasOwnBold}
          questionData={{
            questionId,
            questionName,
            description,
            options,
            childrenQuestions: childrenQuestions || [],
            multimedia,
          }}
          onAnswerChange={(answers) =>
            handleComplexAnswerChange(answers, index)
          }
          primaryColor={primaryColor}
          hasError={unansweredQuestions.includes(index)}
          questionRef={questionRefs[questionId]}
        />
      );
    }

    if (isFile(typeQuestion)) {
      return (
        <FileUploadAnswer
          question={question}
          answer={formValues[index]?.fileObjects || []}
          setAnswer={(files) => {
            const newFormValues = [...formValues];
            const fileNames = files
              .map((f) => (f instanceof File ? f.name : f))
              .filter(Boolean)
              .join(', ');

            newFormValues[index] = {
              ...newFormValues[index],
              value: fileNames,
              fileObjects: files,
              nameQuestion: questionName,
            };
            setFormValues(newFormValues);

            // Remover de preguntas sin responder si tiene archivos
            if (files.length > 0) {
              setUnansweredQuestions((prev) =>
                prev.filter((unansweredIndex) => unansweredIndex !== index),
              );
            }
          }}
          hasError={unansweredQuestions.includes(index)}
          questionRef={questionRefs[questionId]}
          showUploadError={showUploadError}
          hasOwnBold={hasOwnBold}
        />
      );
    }
    if (isTable(typeQuestion)) {
      return (
        <TableQuestion
          ref={questionRefs[questionId]}
          questionData={{
            questionId,
            questionName,
            description,
            tableSetting: tableSetting || { options: [] },
          }}
          onAnswerChange={(answers) => {
            const newFormValues = [...formValues];
            newFormValues[index] = {
              ...answers,
            };
            setFormValues(newFormValues);

            // ✅ CAMBIO: Validar usando selectionTable en lugar de selectionValuesMatriz
            const hasValidAnswers =
              answers.selectionTable?.rows?.length > 0 &&
              answers.selectionTable.rows.some((row) =>
                row.some((cellValue) => cellValue && cellValue.trim() !== ''),
              );

            if (hasValidAnswers) {
              setUnansweredQuestions((prev) =>
                prev.filter((unansweredIndex) => unansweredIndex !== index),
              );
            }
          }}
          primaryColor={primaryColor}
          hasError={unansweredQuestions.includes(index)}
          questionRef={questionRefs[questionId]}
          hasOwnBold={hasOwnBold}
        />
      );
    }

    return null;
  };

  return (
    <FormControl
      key={questionId}
      style={{
        marginTop: isFirstVisibleQuestionInGroup(
          index,
          activeStep,
          visibleQuestions,
        )
          ? '0'
          : '2em',
        marginBottom: '1.1em',
        width: '100%',
        display: shouldDisplay() ? 'inherit' : 'none',
      }}
      className={getQuestionClassName()}
    >
      {renderQuestionComponent()}
    </FormControl>
  );
};

QuestionRenderer.propTypes = {
  question: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

export default QuestionRenderer;
